import React, { useState, useEffect } from 'react';
import "../../css/Vehicles.css"; 
import { Search, Plus, Filter, Edit3, Trash2, X, AlertTriangle, Map, Calendar, User, Clock, Activity, CheckSquare, Clipboard } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../components/Loader';
import axios from 'axios';

function TransportRoutes() {
    const [routes, setRoutes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddingRoute, setIsAddingRoute] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [viewRouteDetails, setViewRouteDetails] = useState(null);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        routeId: null
    });
    const [filters, setFilters] = useState({
        status: "all",
        sortBy: "name"
    });
    const [routeStats, setRouteStats] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });

    // State for managing stops in the new route form
    const [stops, setStops] = useState([{ name: "", time: "" }]);

    // Initial new route state
    const initialRouteState = {
        routeName: "",
        routeNumber: "",
        driver: "",
        busNumber: "",
        startLocation: "",
        endLocation: "",
        departureTime: "",
        estimatedArrival: "",
        stops: [],
        status: "active"
    };

    const [newRoute, setNewRoute] = useState(initialRouteState);

    useEffect(() => {
        fetchRoutes();
    }, []);

    const fetchRoutes = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('https://schoolpro-api.uniums.com/api/transports', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            setRoutes(response.data.routes);
            setRouteStats(response.data.stats);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching routes:", error);
            toast.error(`Error: ${error.response?.data?.message || error.message}`);
            setIsLoading(false);
        }
    };

    const searchRoutes = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('https://schoolpro-api.uniums.com/api/transports/search', {
                params: {
                    term: searchTerm,
                    status: filters.status !== "all" ? filters.status : "",
                    sortBy: filters.sortBy
                },
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            setRoutes(response.data);
            setIsLoading(false);
        } catch (error) {
            console.error("Error searching routes:", error);
            toast.error(`Error: ${error.response?.data?.message || error.message}`);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (searchTerm !== "" || filters.status !== "all" || filters.sortBy !== "name") {
            searchRoutes();
        } else {
            fetchRoutes();
        }
    }, [searchTerm, filters]);

    const addRoute = async (routeData) => {
        setIsLoading(true);
        try {
            const response = await axios.post('https://schoolpro-api.uniums.com/api/transports/add', routeData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            // Add the new route to the state
            setRoutes([...routes, response.data.route]);

            // Update stats
            setRouteStats({
                ...routeStats,
                total: routeStats.total + 1,
                active: routeData.status === 'active' ? routeStats.active + 1 : routeStats.active,
                inactive: routeData.status === 'inactive' ? routeStats.inactive + 1 : routeStats.inactive
            });

            toast.success("Route added successfully!");
            setIsLoading(false);
        } catch (error) {
            console.error("Error adding route:", error);
            toast.error(`Error: ${error.response?.data?.message || error.message}`);
            setIsLoading(false);
        }
    };

    const updateRoute = async (id, routeData) => {
        setIsLoading(true);
        try {
            const response = await axios.put(`https://schoolpro-api.uniums.com/api/transports/${id}`, routeData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const oldRoute = routes.find(r => r._id === id);
            const updatedRoutes = routes.map(route =>
                route._id === id ? response.data.route : route
            );

            setRoutes(updatedRoutes);

            // Update stats if status changed
            if (oldRoute.status !== routeData.status) {
                setRouteStats({
                    ...routeStats,
                    [oldRoute.status]: routeStats[oldRoute.status] - 1,
                    [routeData.status]: routeStats[routeData.status] + 1
                });
            }

            toast.success("Route updated successfully!");
            setIsLoading(false);
        } catch (error) {
            console.error("Error updating route:", error);
            toast.error(`Error: ${error.response?.data?.message || error.message}`);
            setIsLoading(false);
        }
    };

    const deleteRoute = async (id) => {
        setIsLoading(true);
        try {
            await axios.delete(`https://schoolpro-api.uniums.com/api/transports/${id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const routeToDelete = routes.find(r => r._id === id);
            const filteredRoutes = routes.filter(route => route._id !== id);

            setRoutes(filteredRoutes);

            // Update stats
            setRouteStats({
                ...routeStats,
                total: routeStats.total - 1,
                [routeToDelete.status]: routeStats[routeToDelete.status] - 1
            });

            toast.success("Route deleted successfully!");
            setIsLoading(false);
        } catch (error) {
            console.error("Error deleting route:", error);
            toast.error(`Error: ${error.response?.data?.message || error.message}`);
            setIsLoading(false);
        }
    };

    const toggleRouteStatus = async (id) => {
        setIsLoading(true);
        try {
            const response = await axios.patch(`https://schoolpro-api.uniums.com/api/transports/${id}/toggle-status`, {}, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const updatedRoutes = routes.map(route =>
                route._id === id ? response.data.route : route
            );

            const currentRoute = routes.find(r => r._id === id);
            const newStatus = currentRoute.status === 'active' ? 'inactive' : 'active';

            setRoutes(updatedRoutes);

            // Update stats
            setRouteStats({
                ...routeStats,
                active: newStatus === 'active' ? routeStats.active + 1 : routeStats.active - 1,
                inactive: newStatus === 'inactive' ? routeStats.inactive + 1 : routeStats.inactive - 1
            });

            toast.success(`Route status changed to ${newStatus}!`);
            setIsLoading(false);
        } catch (error) {
            console.error("Error toggling route status:", error);
            toast.error(`Error: ${error.response?.data?.message || error.message}`);
            setIsLoading(false);
        }
    };

    // Form handlers
    const handleRouteChange = (e) => {
        const { name, value } = e.target;
        setNewRoute({
            ...newRoute,
            [name]: value
        });
    };

    const handleAddStop = () => {
        setStops([...stops, { name: "", time: "" }]);
    };

    const handleRemoveStop = (index) => {
        const updatedStops = [...stops];
        updatedStops.splice(index, 1);
        setStops(updatedStops);
    };

    const handleStopChange = (index, field, value) => {
        const updatedStops = [...stops];
        updatedStops[index][field] = value;
        setStops(updatedStops);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const routeData = {
            ...newRoute,
            stops: stops.filter(stop => stop.name && stop.time)
        };

        if (selectedRoute) {
            updateRoute(selectedRoute._id, routeData);
        } else {
            addRoute(routeData);
        }
        resetForm();
    };

    const handleEditRoute = (route) => {
        setSelectedRoute(route);
        setNewRoute({
            ...route
        });
        setStops(route.stops.length > 0 ? [...route.stops] : [{ name: "", time: "" }]);
        setIsAddingRoute(true);
    };

    const handleDeleteConfirmation = (id) => {
        setDeleteConfirmation({
            isOpen: true,
            routeId: id
        });
    };

    const confirmDelete = () => {
        deleteRoute(deleteConfirmation.routeId);
        setDeleteConfirmation({
            isOpen: false,
            routeId: null
        });
    };

    const cancelDelete = () => {
        setDeleteConfirmation({
            isOpen: false,
            routeId: null
        });
    };

    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const resetForm = () => {
        setNewRoute(initialRouteState);
        setStops([{ name: "", time: "" }]);
        setSelectedRoute(null);
        setIsAddingRoute(false);
    };

    const openRouteDetails = (route) => {
        setViewRouteDetails(route);
    };

    const closeRouteDetails = () => {
        setViewRouteDetails(null);
    };

    // Get status class for styling
    const getStatusClass = (status) => {
        switch (status) {
            case "active":
                return "vehicles-status-active";
            case "inactive":
                return "vehicles-status-inactive";
            default:
                return "";
        }
    };

    return (
        <div className="vehicles-container">
            {/* Header Section */}
            <ToastContainer />
            <div className="vehicles-header">
                <div className="vehicles-title-container">
                    <div className="vehicles-icon">
                        <Map size={28} />
                    </div>
                    <h1 className="vehicles-title">Transport Routes</h1>
                </div>

                <div className="vehicles-controls">
                    <div className="vehicles-search-container">
                        <Search className="vehicles-search-icon" size={18} />
                        <input
                            type="text"
                            className="vehicles-search-input"
                            placeholder="Search routes by name, number or driver..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <button
                        className="vehicles-filter-toggle-btn"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter size={16} />
                        Filters
                    </button>

                    <button
                        className="vehicles-add-btn"
                        onClick={() => setIsAddingRoute(!isAddingRoute)}
                    >
                        <Plus size={16} />
                        {isAddingRoute ? "Cancel" : "Add New Route"}
                    </button>
                </div>

                {showFilters && (
                    <div className="vehicles-filter-options">
                        <div className="vehicles-filter-group">
                            <label>Status:</label>
                            <select
                                className="vehicles-filter-select"
                                value={filters.status}
                                onChange={(e) => handleFilterChange("status", e.target.value)}
                            >
                                <option value="all">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        <div className="vehicles-filter-group">
                            <label>Sort By:</label>
                            <select
                                className="vehicles-filter-select"
                                value={filters.sortBy}
                                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                            >
                                <option value="name">Route Name</option>
                                <option value="number">Route Number</option>
                                <option value="driver">Driver Name</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Stats Section */}
            <div className="vehicles-stats">
                <div className="vehicles-stat-box">
                    <span className="vehicles-stat-number">{routeStats.total}</span>
                    <span className="vehicles-stat-label">Total Routes</span>
                </div>
                <div className="vehicles-stat-box active">
                    <span className="vehicles-stat-number">{routeStats.active}</span>
                    <span className="vehicles-stat-label">Active</span>
                </div>
                <div className="vehicles-stat-box inactive">
                    <span className="vehicles-stat-number">{routeStats.inactive}</span>
                    <span className="vehicles-stat-label">Inactive</span>
                </div>
            </div>

            {/* Form Section */}
            {isAddingRoute ? (
                <div className="vehicles-form-container">
                    <div className="vehicles-form-header">
                        <h2>{selectedRoute ? "Edit Route" : "Add New Route"}</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="vehicles-form">
                        <div className="vehicles-form-section">
                            <h3 className="vehicles-form-section-title">Basic Information</h3>

                            <div className="vehicles-form-group">
                                <label htmlFor="routeName">Route Name</label>
                                <input
                                    type="text"
                                    id="routeName"
                                    name="routeName"
                                    value={newRoute.routeName}
                                    onChange={handleRouteChange}
                                    required
                                    placeholder="e.g., Downtown Express"
                                />
                            </div>

                            <div className="vehicles-form-row">
                                <div className="vehicles-form-group">
                                    <label htmlFor="routeNumber">Route Number</label>
                                    <input
                                        type="text"
                                        id="routeNumber"
                                        name="routeNumber"
                                        value={newRoute.routeNumber}
                                        onChange={handleRouteChange}
                                        required
                                        placeholder="e.g., R101"
                                    />
                                </div>

                                <div className="vehicles-form-group">
                                    <label htmlFor="busNumber">Bus Number</label>
                                    <input
                                        type="text"
                                        id="busNumber"
                                        name="busNumber"
                                        value={newRoute.busNumber}
                                        onChange={handleRouteChange}
                                        required
                                        placeholder="e.g., BUS-123"
                                    />
                                </div>
                            </div>

                            <div className="vehicles-form-group">
                                <label htmlFor="driver">Driver Name</label>
                                <input
                                    type="text"
                                    id="driver"
                                    name="driver"
                                    value={newRoute.driver}
                                    onChange={handleRouteChange}
                                    required
                                    placeholder="e.g., John Smith"
                                />
                            </div>
                        </div>

                        <div className="vehicles-form-section">
                            <h3 className="vehicles-form-section-title">Route Details</h3>

                            <div className="vehicles-form-row">
                                <div className="vehicles-form-group">
                                    <label htmlFor="startLocation">Start Location</label>
                                    <input
                                        type="text"
                                        id="startLocation"
                                        name="startLocation"
                                        value={newRoute.startLocation}
                                        onChange={handleRouteChange}
                                        required
                                        placeholder="e.g., Main Campus"
                                    />
                                </div>

                                <div className="vehicles-form-group">
                                    <label htmlFor="endLocation">End Location</label>
                                    <input
                                        type="text"
                                        id="endLocation"
                                        name="endLocation"
                                        value={newRoute.endLocation}
                                        onChange={handleRouteChange}
                                        required
                                        placeholder="e.g., North Terminal"
                                    />
                                </div>
                            </div>

                            <div className="vehicles-form-row">
                                <div className="vehicles-form-group">
                                    <label htmlFor="departureTime">Departure Time</label>
                                    <input
                                        type="time"
                                        id="departureTime"
                                        name="departureTime"
                                        value={newRoute.departureTime}
                                        onChange={handleRouteChange}
                                        required
                                    />
                                </div>

                                <div className="vehicles-form-group">
                                    <label htmlFor="estimatedArrival">Estimated Arrival</label>
                                    <input
                                        type="time"
                                        id="estimatedArrival"
                                        name="estimatedArrival"
                                        value={newRoute.estimatedArrival}
                                        onChange={handleRouteChange}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="vehicles-form-section">
                            <h3 className="vehicles-form-section-title">Stops</h3>
                            <div className="vehicles-form-stops">
                                {stops.map((stop, index) => (
                                    <div key={index} className="vehicles-form-stop-item">
                                        <div className="vehicles-form-stop-fields">
                                            <div className="vehicles-form-group">
                                                <label>Stop Name</label>
                                                <input
                                                    type="text"
                                                    value={stop.name}
                                                    onChange={(e) => handleStopChange(index, 'name', e.target.value)}
                                                    placeholder="e.g., Central Park"
                                                    required
                                                />
                                            </div>
                                            <div className="vehicles-form-group">
                                                <label>Time</label>
                                                <input
                                                    type="time"
                                                    value={stop.time}
                                                    onChange={(e) => handleStopChange(index, 'time', e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        {stops.length > 1 && (
                                            <button
                                                type="button"
                                                className="vehicles-remove-stop-btn"
                                                onClick={() => handleRemoveStop(index)}
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="vehicles-add-stop-btn"
                                    onClick={handleAddStop}
                                >
                                    <Plus size={16} /> Add Stop
                                </button>
                            </div>
                        </div>

                        <div className="vehicles-form-section">
                            <h3 className="vehicles-form-section-title">Status</h3>
                            <div className="vehicles-form-group">
                                <label htmlFor="status">Route Status</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={newRoute.status}
                                    onChange={handleRouteChange}
                                    required
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <div className="vehicles-form-actions">
                            <button
                                type="button"
                                className="vehicles-cancel-btn"
                                onClick={resetForm}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="vehicles-submit-btn"
                            >
                                {selectedRoute ? "Update Route" : "Save Route"}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <>
                    {/* Routes Table */}
                    {routes.length > 0 ? (
                        <div className="vehicles-table-container">
                            <table className="vehicles-table">
                                <thead>
                                    <tr>
                                        <th>Route Details</th>
                                        <th>Driver</th>
                                        <th>Bus Number</th>
                                        <th>Start → End</th>
                                        <th>Times</th>
                                        <th>Stops</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {routes.map((route) => (
                                        <tr key={route._id} className={`vehicles-row-${route.status}`}>
                                            <td className="vehicles-info-cell">
                                                <div className="vehicles-info-primary" onClick={() => openRouteDetails(route)}>
                                                    {route.routeName}
                                                </div>
                                                <div className="vehicles-info-secondary">
                                                    {route.routeNumber}
                                                </div>
                                            </td>
                                            <td>{route.driver}</td>
                                            <td>{route.busNumber}</td>
                                            <td>
                                                <div className="vehicles-route-locations">
                                                    <div>{route.startLocation}</div>
                                                    <div className="vehicles-route-arrow">→</div>
                                                    <div>{route.endLocation}</div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="vehicles-maintenance-info">
                                                    <div className="vehicles-maintenance-date">
                                                        <Clock size={14} className="vehicles-icon" />
                                                        Dep: {route.departureTime}
                                                    </div>
                                                    <div className="vehicles-maintenance-date">
                                                        <Clock size={14} className="vehicles-icon" />
                                                        Arr: {route.estimatedArrival}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{route.stops.length} stops</td>
                                            <td>
                                                <span
                                                    className={`vehicles-status-badge ${getStatusClass(route.status)}`}
                                                    onClick={() => toggleRouteStatus(route._id)}
                                                >
                                                    {route.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="vehicles-actions">
                                                    <button
                                                        className="vehicles-action-btn vehicles-edit-btn"
                                                        onClick={() => handleEditRoute(route)}
                                                    >
                                                        <Edit3 size={16} />
                                                    </button>
                                                    <button
                                                        className="vehicles-action-btn vehicles-delete-btn"
                                                        onClick={() => handleDeleteConfirmation(route._id)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="vehicles-empty">
                            <div className="vehicles-empty-icon">
                                <AlertTriangle size={48} />
                            </div>
                            <h3 className="vehicles-empty-text">No routes found</h3>
                            <p>Try adjusting your search or filters</p>
                            {searchTerm && (
                                <button
                                    className="vehicles-reset-search"
                                    onClick={() => setSearchTerm("")}
                                >
                                    Clear Search
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Route Details Modal */}
            {viewRouteDetails && (
                <div className="vehicles-modal-overlay">
                    <div className="vehicles-modal vehicles-details-modal">
                        <div className="vehicles-modal-header">
                            <h3>Route Details</h3>
                            <button
                                className="vehicles-modal-close"
                                onClick={closeRouteDetails}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="vehicles-details-content">
                            <div className="vehicles-details-header">
                                <div className="vehicles-header-left">
                                    <h2>{viewRouteDetails.routeName}</h2>
                                    <span className="vehicles-vehicle-number">{viewRouteDetails.routeNumber}</span>
                                </div>
                                <span className={`vehicles-status-badge ${getStatusClass(viewRouteDetails.status)}`}>
                                    {viewRouteDetails.status}
                                </span>
                            </div>

                            <div className="vehicles-detail-sections">
                                <div className="vehicles-detail-section">
                                    <h3 className="vehicles-detail-section-title">
                                        <Map size={18} />
                                        Basic Information
                                    </h3>
                                    <div className="vehicles-detail-grid">
                                        <div className="vehicles-detail-item">
                                            <div className="vehicles-detail-label">Driver</div>
                                            <div className="vehicles-detail-value">{viewRouteDetails.driver}</div>
                                        </div>
                                        <div className="vehicles-detail-item">
                                            <div className="vehicles-detail-label">Bus Number</div>
                                            <div className="vehicles-detail-value">{viewRouteDetails.busNumber}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="vehicles-detail-section">
                                    <h3 className="vehicles-detail-section-title">
                                        <Activity size={18} />
                                        Route Information
                                    </h3>
                                    <div className="vehicles-detail-grid">
                                        <div className="vehicles-detail-item">
                                            <div className="vehicles-detail-label">Start Location</div>
                                            <div className="vehicles-detail-value">{viewRouteDetails.startLocation}</div>
                                        </div>
                                        <div className="vehicles-detail-item">
                                            <div className="vehicles-detail-label">End Location</div>
                                            <div className="vehicles-detail-value">{viewRouteDetails.endLocation}</div>
                                        </div>
                                        <div className="vehicles-detail-item">
                                            <div className="vehicles-detail-label">Departure Time</div>
                                            <div className="vehicles-detail-value">{viewRouteDetails.departureTime}</div>
                                        </div>
                                        <div className="vehicles-detail-item">
                                            <div className="vehicles-detail-label">Estimated Arrival</div>
                                            <div className="vehicles-detail-value">{viewRouteDetails.estimatedArrival}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="vehicles-detail-section">
                                    <h3 className="vehicles-detail-section-title">
                                        <Clipboard size={18} />
                                        Stops
                                    </h3>
                                    <div className="vehicles-detail-stops">
                                        {viewRouteDetails.stops.length > 0 ? (
                                            <div className="vehicles-stops-list">
                                                {viewRouteDetails.stops.map((stop, index) => (
                                                    <div className="vehicles-stop-item" key={index}>
                                                        <div className="vehicles-stop-marker">{index + 1}</div>
                                                        <div className="vehicles-stop-info">
                                                            <div className="vehicles-stop-name">{stop.name}</div>
                                                            <div className="vehicles-stop-time">{stop.time}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="vehicles-detail-item full-width">
                                                <div className="vehicles-detail-value">No stops defined for this route</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="vehicles-details-actions">
                                <button
                                    className="vehicles-edit-btn details-btn"
                                    onClick={() => {
                                        handleEditRoute(viewRouteDetails);
                                        closeRouteDetails();
                                    }}
                                >
                                    <Edit3 size={16} />
                                    Edit Route
                                </button>
                                <button
                                    className="vehicles-status-btn details-btn"
                                    onClick={() => {
                                        toggleRouteStatus(viewRouteDetails._id);
                                        closeRouteDetails();
                                    }}
                                >
                                    <Activity size={16} />
                                    Toggle Status
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.isOpen && (
                <div className="vehicles-modal-overlay">
                    <div className="vehicles-modal vehicles-delete-modal">
                        <div className="vehicles-modal-header">
                            <h3>Confirm Deletion</h3>
                            <button
                                className="vehicles-modal-close"
                                onClick={cancelDelete}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="vehicles-modal-content">
                            <div className="vehicles-delete-warning">
                                <AlertTriangle size={48} className="vehicles-warning-icon" />
                                <p>Are you sure you want to delete this route? This action cannot be undone.</p>
                            </div>
                            <div className="vehicles-modal-actions">
                                <button
                                    className="vehicles-cancel-btn"
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="vehicles-delete-confirm-btn"
                                    onClick={confirmDelete}
                                >
                                    Yes, Delete Route
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {isLoading && <Loader />}
        </div>
    );
}

export default TransportRoutes;